import Editor, { editorEmptyString } from "Dummies/TestImpl/TestEditor";
import { ControllerHelper, LanguageStringDownloadDto, TranslationController } from 'collaboration-service';
import LabelInfo from 'components/LabelInfo';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { Button, Flag, ISO_3166, ImgIcons, Label, LanguageSelect, LanguageSelectProps, Theme, getColor, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import { StringMap } from "quill";
import * as React from 'react';
import { useSelector } from "react-redux";
import { CellProps, Column, useBlockLayout, usePagination, useResizeColumns, useTable } from 'react-table';
import { ApplicationState, useFrontEndConfig } from "services/ApplicationState/ApplicationState";
import { languages } from 'services/Config';
import { shallowCompare, stripHtml } from 'services/Helpers';
import { useCheckWhereIAm } from "services/Helpers/RoutingHelper";
import { useImgI18N } from 'services/ImgI18N';
import { useThemePart } from 'services/useAppTheme';
import { ThemeContext } from "styled-components";
import { EPLContainerEdit, EPLEditContainer, EPLEditContainerTextAreaInput, EPLLabelContainer } from '../EditPartLabelAndEditor';
import { BaseEditItem } from '../GeneralPostEdit';
import { useGPEState } from '../Helpers/UseGPEState';
import { MissingTexts, PostEditElementProps } from '../TypeImplementations/PostEdit';
import { EditorsSC as T, columnActionWidth, columnFlagWidth } from './EditorsSC';

interface EditLanguageStringsProps<T> extends PostEditElementProps<T, LanguageStringDownloadDto[]> {
    editorFormats?: string[];
    noHtml?: boolean;
    hideToolbar?: boolean;
    asInput?: boolean;
    type?: string;
    widthScaling?: number;
    saveText?: string;
    saveIcon?: ImgIcons;
}
interface EditLanguageStringsBaseProps<T> extends Omit<EditLanguageStringsProps<T>, "getter"> {
    value: LanguageStringDownloadDto[];
    setValue: (val: LanguageStringDownloadDto[] | ((val: LanguageStringDownloadDto[]) => LanguageStringDownloadDto[])) => void;
    resetValue?: () => void;
    onEdit?: (editing: boolean) => void;
    editMode?: boolean;
    fixWidth?: number;
    initialOpen?: boolean;
    hideTitleBarIfEmpty?: boolean;
    menuPortal?: LanguageSelectProps<false, false>["menuPortalTarget"];
    allowDeleteAll?: boolean;
    savingText?: string;
    savingIcon?: ImgIcons;
}

interface LanguageTableProps {
    widthScaling?: number;
    editValue?: LanguageStringDownloadDto;
    value: LanguageStringDownloadDto[];
    setEdit?: (value: number) => void;
    deleteEntry?: (value: number) => void;
    type?: string;
    addNew?: () => void;
    editMode?: boolean;
    autoTranslate?: () => void;
    removeAutoTranslations?: () => void;
    fixWidth?: number;
    languages: string[];
    allowDeleteAll?: boolean;
    showPagination?: boolean;
    width?: string;
    pageSize?: number;
}

export const LanguageStringsTable = (p: LanguageTableProps) => {
    const { widthScaling, editValue, value, setEdit, deleteEntry, type, addNew, editMode, autoTranslate, removeAutoTranslations, fixWidth, languages, allowDeleteAll, /*showPagination,*/ width, pageSize } = p;

    const { t, createT } = useImgI18N("admin");
    const t_g = React.useMemo(() => createT("general"), [createT]);
    const [selectedRowId, setSelectedRowId] = React.useState<{ id: string, time: number } | undefined>();
    const cWidth = useThemePart(x => x.content.width);
    const [finalWidth, setFinalWidth] = React.useState(fixWidth ?? cWidth);
    const check = useCheckWhereIAm();
    const isCmsFile = check("cms_file");

    React.useEffect(() => {
        if (fixWidth !== undefined) {
            setFinalWidth(fixWidth);
        }
    }, [fixWidth]);

    const isMobile = useBelowBreakpoint("tablet");
    const hasActions = setEdit !== undefined && deleteEntry !== undefined;
    const x = isCmsFile ? (editMode ? 32 : columnFlagWidth + columnActionWidth / 2 + 10) : 0;
    const w = (finalWidth) - x - (hasActions ? columnActionWidth : 0) - columnFlagWidth;


    const columns = React.useMemo(() => {
        const toRet: Array<Column<LanguageStringDownloadDto> & { padding?: Number, align?: "left" | "right" | "center" }> = [];
        if (hasActions)
            toRet.push({
                id: "Actions",
                padding: 0,
                width: columnActionWidth,
                Header: () => t("actions"),
                align: "left",
                Cell: (cell: CellProps<LanguageStringDownloadDto>) => {
                    const [delConfirmed, setDelConfirmed] = React.useState<boolean>(false);
                    const edit = setEdit ? () => {
                        setEdit(cell.row.index);
                    } : undefined;
                    const del = deleteEntry ? () => {
                        deleteEntry(cell.row.index);
                    } : undefined;

                    return (
                        <div style={{ display: "grid", gridTemplateColumns: "max-content max-content" }}
                            onMouseLeave={() => setDelConfirmed(false)}
                        >
                            <Button
                                icon={delConfirmed ? "times" : "delete"}
                                kind={delConfirmed ? "secondary" : ((value.length < 2 && !allowDeleteAll) ? "fullTransparentButton" : "transparentButton")}
                                iconColor={delConfirmed ? undefined : (value.length < 2 && !allowDeleteAll) ? "@middleLightGrey" : "@accentRed"}
                                onClick={() => setDelConfirmed(!delConfirmed)}
                                disabled={(value.length < 2 && !allowDeleteAll)}
                                tooltip={delConfirmed ? undefined : {
                                    tooltipText: t((value.length < 2 && !allowDeleteAll) ? "delete not possible, as at least one entry must be present " : "delete entry"),
                                    position: delConfirmed ? "left" : "bottom"
                                }}
                                cursor={(value.length < 2 && !allowDeleteAll) ? "default" : "pointer"}
                            />
                            <Button
                                icon={delConfirmed ? "delete" : "edit"}
                                kind={delConfirmed ? "primary" : "transparentButton"}
                                iconColor={delConfirmed ? undefined : "@accentBlue"}
                                onClick={delConfirmed ? del : edit}
                                tooltip={{ tooltipText: t(delConfirmed ? "yes, delete entry" : "edit entry"), show: delConfirmed, offsetX: delConfirmed ? -14 : undefined, position: delConfirmed ? "bottom-start" : "right", background: delConfirmed ? "@accentRed" : undefined, fontColor: delConfirmed ? "@mainBackground" : undefined }}
                            />
                        </div>
                    )
                },
            })
        toRet.push({
            id: "Flag",
            width: columnFlagWidth,
            align: "left",
            Header: () => t("language"),
            Cell: (p: CellProps<LanguageStringDownloadDto>) => {
                const l = p.data[p.row.index].lng;
                if (!l) {
                    return <div>{t("language not specified")}</div>;
                }
                const fs = l.split("-");
                const f = (fs.length > 1 ? fs[1] : fs[0].toUpperCase()) as ISO_3166;
                return (
                    <>
                        <span style={{ textAlign: "left", marginLeft: 5, width: 25 }}>
                            <Flag name={f} size={16} />
                        </span>
                        <span style={{ marginTop: 1, marginLeft: 10 }}>
                            {t_g(fs[0])}
                        </span>
                    </>
                );
            },

        },
            {
                id: "Text",
                Header: () => t("text"),
                accessor: d => d.text,
                width: w
            })

        return toRet;
    }, [deleteEntry, t, t_g, w, setEdit, hasActions, value.length, allowDeleteAll]);

    const itemsPerPage = pageSize ?? 5;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,

    } = useTable({ columns, data: value ?? [], initialState: { pageSize: itemsPerPage } },
        usePagination,
        useBlockLayout,
        useResizeColumns,

    );

    return (

        <T.LanguageStringEditorSC.container
            hidden={false}
            style={{
                // overflowX: "auto",
                background: "#fff"
            }}
            widthScaling={widthScaling}
        >

            {(page.length > 0 || editMode) &&
                <T.LanguageStringEditorSC.table {...getTableProps() as any} style={{ borderTop: "none" }}>
                    <T.LanguageStringEditorSC.thead >
                        {headerGroups.map(headerGroup => (
                            <T.LanguageStringEditorSC.tr {...headerGroup.getHeaderGroupProps() as any}>
                                {headerGroup.headers.map(column => {
                                    return (
                                        <T.LanguageStringEditorSC.th {...column.getHeaderProps() as any} topBorder>
                                            {column.render('Header')}
                                        </T.LanguageStringEditorSC.th>
                                    )
                                }
                                )}
                            </T.LanguageStringEditorSC.tr>
                        ))}
                    </T.LanguageStringEditorSC.thead>
                    <T.LanguageStringEditorSC.tbody {...getTableBodyProps() as any}>
                        {page.map(row => {
                            prepareRow(row)
                            const isSelected = row.id === selectedRowId?.id;
                            const onChg = (e: any) => {
                                if (setEdit && setSelectedRowId)
                                    if (isSelected) {
                                        const deltaT = (new Date().getTime()) - (selectedRowId?.time ?? 0);
                                        if (deltaT <= 500) {
                                            setEdit(row.index);
                                        }
                                        else
                                            setSelectedRowId({ id: row.id, time: new Date().getTime() });
                                    }
                                    else
                                        setSelectedRowId({ id: row.id, time: new Date().getTime() });
                            }
                            return (
                                <T.LanguageStringEditorSC.tr {...row.getRowProps() as any} selected={row.isSelected}>
                                    {row.cells.map(cell => {
                                        if (cell.column.id === "Text")
                                            return (
                                                <T.LanguageStringEditorSC.td {...cell.getCellProps(T.LanguageStringEditorSC.cellProps) as any} onClick={onChg}>
                                                    <T.Styles>
                                                        <SafeHTML html={cell.value} />
                                                    </T.Styles>
                                                </T.LanguageStringEditorSC.td>
                                            )
                                        else
                                            return (
                                                <T.LanguageStringEditorSC.td {...cell.getCellProps(T.LanguageStringEditorSC.cellProps) as any}>
                                                    {cell.render('Cell')}
                                                </T.LanguageStringEditorSC.td>
                                            )
                                    })}
                                </T.LanguageStringEditorSC.tr>
                            )
                        })}
                    </T.LanguageStringEditorSC.tbody>
                </T.LanguageStringEditorSC.table>

            }
            {/* {((showPagination && value.length > itemsPerPage) || (editValue === undefined && editMode)) && */}
            {editValue === undefined &&
                <T.Grid shadowed style={{ width: width ?? "100%", zIndex: 5000, gridTemplateColumns: "max-content 1fr" }} bordered>
                    <div>
                        {value.length > itemsPerPage && <>
                            <Button floated='left' icon="chevron left double" onClick={() => gotoPage(0)} kind={!canPreviousPage ? "cancelButton" : "halfTransparentButton"} disabled={!canPreviousPage} tooltip={!canPreviousPage ? undefined : { tooltipText: "first page", position: "top" }} />
                            <Button floated='left' icon="chevron left" onClick={() => previousPage()} kind={!canPreviousPage ? "cancelButton" : "halfTransparentButton"} disabled={!canPreviousPage} tooltip={!canPreviousPage ? undefined : { tooltipText: "previous page", position: "top" }} />
                            <Button floated='left' icon="chevron right" onClick={() => nextPage()} kind={!canNextPage ? "cancelButton" : "halfTransparentButton"} disabled={!canNextPage} tooltip={!canNextPage ? undefined : { tooltipText: "next page", position: "top" }} />
                            <Button floated='left' icon="chevron right double" onClick={() => gotoPage(pageCount - 1)} kind={!canNextPage ? "cancelButton" : "halfTransparentButton"} disabled={!canNextPage} tooltip={!canNextPage ? undefined : { tooltipText: "last page", position: "top" }} />
                        </>
                        }
                        {value.length === 0 && type &&
                            <div style={{ paddingLeft: 10, fontSize: "0.9em" }}>{t(type === "Headlines" ? MissingTexts.headlines : type === "Descriptions" ? MissingTexts.descriptions : "missing input")}</div>
                        }
                    </div>
                    <div >
                        {addNew && value.length > 0 &&
                            <Button
                                floated="right"
                                icon="add"
                                kind={value.length !== 0 ? "secondary" : "primary"}
                                onClick={addNew}
                                content={isMobile ? undefined : value.length > 0 ? t("add") : t("add new language variant")}
                                tooltip={value.length > 0 ? { tooltipText: t("add another language variant"), position: "top" } : undefined}
                                disabled={languages.length === 0}
                            />
                        }
                        {autoTranslate && value.length > 0 &&
                            <Button
                                floated="right"
                                icon="add"
                                kind={"halfTransparentButton"}
                                onClick={autoTranslate}
                                content={isMobile ? undefined : t("auto add languages")}
                                tooltip={value.length > 0 ? { tooltipText: t("auto translate to missing languages"), position: "top" } : undefined}
                            />
                        }
                        {removeAutoTranslations && _.countBy(value, v => v.autoTranslation === true).true > 0 &&
                            <Button
                                floated="right"
                                icon="minus"
                                kind={"halfTransparentButton"}
                                onClick={autoTranslate}
                                content={isMobile ? undefined : t("remove auto languages")}
                                tooltip={value.length > 0 ? { tooltipText: t("remove auto translated languages"), position: "top" } : undefined}
                            />
                        }
                    </div>
                </T.Grid>
            }
        </T.LanguageStringEditorSC.container>
    )
}

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
});

export const EditLanguageStringsBase = <T extends any = any>(p: EditLanguageStringsBaseProps<T>) => {
    const { contentLanguage } = useSelector(mapper, shallowCompare);
    const { editorFormats, noHtml, hideToolbar, value, setValue, /*resetValue, */asInput, type, widthScaling, onEdit, editMode, fixWidth, /*initialOpen,*/ hideTitleBarIfEmpty, menuPortal, allowDeleteAll, savingText, savingIcon } = p;
    const { t, createT } = useImgI18N("admin");
    const t_g = React.useMemo(() => createT("general"), [createT]);
    const [edit, setEdit] = React.useState<number>();
    const [editValue, setEditValue] = React.useState<LanguageStringDownloadDto>();
    const theme = React.useContext<Theme>(ThemeContext);
    const config = useFrontEndConfig();

    const deleteEntry = React.useMemo(() => (idx: number) => {
        const v = _.clone(value);
        v.splice(idx, 1);
        setValue(v);
    }, [value, setValue]);

    const toolbarFormat: StringMap = {
        'header': [undefined],
        'bold': [true, false],
        'italic': [true, false],
        'underline': [true, false],
        'list': ['ordered', 'bullet'],
    };

    const lngs = React.useMemo(() => {
        return _.filter([...languages, "de-CH"], l => _.find(value, v => v.lng === l) === undefined);
    }, [value])


    const position = (() => {
        if (contentLanguage && lngs.includes(contentLanguage)) {
            return lngs.indexOf(contentLanguage);
        } else {
            return 0;
        }
    })();

    React.useEffect(() => {
        if (edit !== undefined) {
            if (edit >= 0) {
                setEditValue(value[edit]);
            } else {
                const defaultLng = lngs[position];
                setEditValue({ text: "<p></p>", lng: defaultLng });
            }
            if (onEdit)
                onEdit(true);
        } else {
            setEditValue(undefined);
            if (onEdit)
                onEdit(false);
        }
    }, [edit, value, lngs, onEdit, position])

    const addNew = React.useCallback(() => {
        setEdit(-1);
    }, []);

    React.useEffect(() => {
        if (value.length === 0)
            addNew();
        else
            setEdit(undefined);
    }, [addNew, value.length]);

    const save = () => {
        if (editValue && editValue.lng) {
            const toSet = _.clone(editValue);
            if (noHtml)
                toSet.text = stripHtml(toSet.text);
            const v = _.clone(value);
            if (edit !== undefined && edit >= 0)
                v[edit] = toSet;
            else
                v.push(toSet)
            setValue(v);
        } else {
            console.error("Language not defined!");
        }
        setEdit(undefined);
    }

    const saveDisabled = edit === undefined || (edit >= 0 ? value[edit].text === editValue?.text : ("<p></p>" === editValue?.text || editorEmptyString === editValue?.text));

    const cancel = () => {
        setEdit(undefined);
    }

    const chg = (text: string) => {
        if (editValue) {
            setEditValue({ ...editValue, text });
        }
    }

    const chgEditLng = (lng: string) => {
        if (editValue)
            setEditValue({ ...editValue, lng });
    }

    const valueLng = React.useMemo(() => editValue?.lng ?? (contentLanguage !== undefined && lngs.includes(contentLanguage) ? contentLanguage : lngs[0]), [contentLanguage, editValue?.lng, lngs]);
    const languageList = React.useMemo(() => lngs.includes(valueLng) ? lngs : (valueLng ? [...lngs, valueLng] : lngs), [valueLng, lngs]);;

    const autoTranslate = React.useMemo(() => () => {
        const l = async () => {
            const res = await ControllerHelper.singleCall({ toLngs: languageList, toTranslate: value }, TranslationController.TranslateLngString, true);
            if (res)
                setValue(res);
        }
        l();
    }, [languageList, setValue, value])

    const removeAutoTranslations = React.useMemo(() => () => {
        setValue(v => _.filter(v, vv => !vv.autoTranslation));
    }, [setValue]);

    const showTitleBar = hideTitleBarIfEmpty ? value.length > 0 : true;

    return (
        <>{showTitleBar &&
            <LanguageStringsTable
                editValue={editValue}
                value={value}
                addNew={addNew}
                deleteEntry={editValue === undefined ? deleteEntry : undefined}
                setEdit={setEdit}
                type={type}
                widthScaling={widthScaling}
                editMode={editMode}
                fixWidth={fixWidth}
                autoTranslate={config.enableAutoTranslationForLngStrings ? autoTranslate : undefined}
                removeAutoTranslations={config.enableAutoTranslationForLngStrings ? removeAutoTranslations : undefined}
                languages={languageList}
                allowDeleteAll={allowDeleteAll}
            />
        }
            <T.LanguageStringEditorSC.container
                hidden={editValue === undefined}
                style={{
                    marginTop: value.length === 0 ? 5 : 0,
                    overflowX: "auto",
                    padding: 10,
                    border: `1px solid ${getColor({ theme }, "@middleLightGrey")}`,
                    borderTop: showTitleBar ? (value.length === 0 ? `1px solid ${getColor({ theme }, "@middleLightGrey")}` : "none") : `1px solid ${getColor({ theme }, "@middleLightGrey")}`,
                    background: getColor({ theme }, "@veryLightGrey"),
                    paddingBottom: 15,
                    width: "100%"
                }}>
                <EPLContainerEdit style={{ marginTop: 5 }}>
                    <EPLLabelContainer style={{ paddingRight: 16 }}>
                        <Label content={t("language")} icon="language" />
                        <LabelInfo infoText={t("edit language strings - language description")} />
                    </EPLLabelContainer>
                    <EPLEditContainer>
                        <LanguageSelect
                            languageList={languageList}
                            translationFunction={t_g}
                            value={valueLng}
                            isDisabled={_.find(value, v => v.lng === editValue?.lng) !== undefined}
                            onChange={chgEditLng}
                            captureMenuScroll
                            maxMenuHeight={180}
                            menuPortalTarget={menuPortal}
                        />
                    </EPLEditContainer>
                </EPLContainerEdit>

                <EPLContainerEdit >
                    <EPLLabelContainer style={{ paddingRight: 16 }}>
                        <Label content={t("edit {{type}}", { type: type ?? undefined })} icon="edit" />
                        <LabelInfo infoText={t("edit language strings - headline description")} />
                    </EPLLabelContainer>
                    <EPLEditContainer>
                        {asInput ?
                            <EPLEditContainerTextAreaInput>
                                <Editor
                                    value={editValue?.text ?? "<p></p>"}
                                    onChange={chg}
                                    formats={editorFormats}
                                    hideToolbar={hideToolbar}
                                    toolbarFormat={toolbarFormat}
                                />
                            </EPLEditContainerTextAreaInput>
                            :
                            <Editor
                                value={editValue?.text ?? ""}
                                onChange={chg}
                                height={80}
                                formats={editorFormats}
                                hideToolbar={hideToolbar}
                                toolbarFormat={toolbarFormat}
                            />
                        }
                    </EPLEditContainer>
                </EPLContainerEdit>
                <EPLContainerEdit style={{ marginTop: asInput ? -5 : 0 }}>
                    <EPLLabelContainer />
                    <EPLEditContainer>
                        <T.Grid shadowed bordered style={{ width: "100%" }} className="X12">
                            <div>
                                {value.length > 0 &&
                                    <Button floated='left' icon="times" kind={saveDisabled ? "secondary" : "halfTransparentButton"}
                                        onClick={cancel} content="cancel"
                                    />
                                }
                            </div>
                            <div>
                                <Button
                                    floated='right'
                                    icon={savingIcon ?? "save"}
                                    disabled={saveDisabled}
                                    onClick={save}
                                    content={savingText ?? t("save")}
                                    kind={saveDisabled ? "blank" : "primary"}
                                    tooltip={saveDisabled ? { tooltipText: t("no changes have been detected"), position: "top" } : undefined}
                                />
                            </div>
                        </T.Grid>
                    </EPLEditContainer>
                </EPLContainerEdit>
            </T.LanguageStringEditorSC.container>
        </>
    )
}
const EditLanguageStrings = <T extends BaseEditItem = BaseEditItem>(p: EditLanguageStringsProps<T>) => {
    const { getter, type, saveText, saveIcon, ...rest } = p;
    const { value, setValue, resetValue } = useGPEState(getter);
    return <EditLanguageStringsBase<T> {...rest} type={type} value={value} setValue={setValue} resetValue={resetValue} savingText={saveText} savingIcon={saveIcon} />

}
export default EditLanguageStrings;